import produce from 'immer';
import {useRef, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {AnalyticsParameterValueOperation} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {EmbeddableParameterType} from 'insights/embeddings/EmbeddingConstants';
import getParameterType from 'insights/parameters/data/getParameterType';
import sortParameterConfigs from 'insights/parameters/data/sortParameterConfigs';
import {JSONValue} from 'util/Types';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';
import {ParameterValueTemplateFormQuery} from './__generated/ParameterValueTemplateFormQuery';

type AnalyticsDashboard = ExtractByTypename<
  ParameterValueTemplateFormQuery['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsParameterConfig = AnalyticsDashboard['parameterConfigs'][number];

function withUpdatedParameterValueTemplate(
  input: Array<ValidatedParameterValueTemplate>,
  config: AnalyticsParameterConfig,
  valueJson: JSONValue,
  valueOperation: AnalyticsParameterValueOperation,
  valid: boolean
): Array<ValidatedParameterValueTemplate> {
  return produce(input, (draftState) => {
    const validatedValueTemplate = draftState.find((param) => param.valueTemplate.config.id === config.id);
    if (validatedValueTemplate) {
      validatedValueTemplate.valueTemplate.valueJson = valueJson;
      validatedValueTemplate.valueTemplate.valueOperation = valueOperation;
      validatedValueTemplate.valid = valid;
    } else {
      draftState.push({
        valueTemplate: {
          __typename: 'AnalyticsParameterValueTemplateNode',
          config,
          valueJson,
          valueOperation,
        },
        valid,
      });
    }
  });
}

// Replaces (inaccessible or accessible) config in GraphQL fragment with always accessible config
type AccessibleParameterValueTemplateGraphQL = AnalyticsParameterValueTemplateGraphQL & {
  config: AnalyticsParameterConfig;
};

export interface ValidatedParameterValueTemplate {
  valueTemplate: AccessibleParameterValueTemplateGraphQL;
  valid: boolean;
}

export interface ParameterValueTemplateFormState {
  parameterValueTemplates: Array<AccessibleParameterValueTemplateGraphQL>;
  hasInvalidInputs: boolean;
  setToValueTemplates: (valueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>) => void;
  setToDashboardValues: (dashboard: Pick<AnalyticsDashboard, 'parameterValues' | 'parameterConfigs'>) => void;
  updateParameterValueTemplate: (
    config: AnalyticsParameterConfig,
    valueJson: JSONValue,
    valueOperation: AnalyticsParameterValueOperation,
    valid: boolean
  ) => void;
  getParameterValueTemplate(configId: string): AccessibleParameterValueTemplateGraphQL | undefined;
}

/**
 * Hook used to track the state of embedded dashboard parameter value template forms.
 *
 * The hook maintains the values for each config, tracking the value operation & value json for the config
 * as well as the validity of the config value. The hook returns the current state as well as functions to
 * get, set, and update the state.
 *
 * Returns:
 * - parameterValueTemplates - array of current form values. This array is sparse in that there is
 *   only one element per set value template, not one per parameter config.
 * - hasInvalidInputs - whether or not any of the form inputs currently has an invalid value
 * - setToDashboardValues - function to fill the form's input values to any existing parameter values on the
 *   the input dashboard
 * - updateParameterValueTemplate - function to update the value & validity of the input parameter config
 * - getParameterValueTemplate - function to get the value of the input parameter config ID.
 */
export default function useParameterValueTemplateFormState(
  embeddingType: EmbeddableParameterType
): ParameterValueTemplateFormState {
  /**
   * If there is a date input in the parameter form, the date input will bind event listeners to the window
   * with the initial useState value, and will never be updated. The consequence of this is that
   * when selecting a date from the date selector, the state seems to "reset" to having no values at all
   * except the date selected. That is because the date input's version of parameterValueTemplates is
   * the empty list (the initial value).
   *
   * To avoid this, using useRef gives the date input an object with the state inside of it so that only
   * the current version of the state is used.
   *
   * See https://stackoverflow.com/questions/56511176/state-being-reset/56554056.
   */
  const [parameterValueTemplates, _setParameterValueTemplateFormState] = useState<
    Array<ValidatedParameterValueTemplate>
  >([]);
  const parameterValueTemplatesRef = useRef<Array<ValidatedParameterValueTemplate>>(parameterValueTemplates);

  function setParameterValueTemplateFormState(newState: Array<ValidatedParameterValueTemplate>): void {
    parameterValueTemplatesRef.current = newState;
    _setParameterValueTemplateFormState(newState);
  }

  const [hasInvalidInputs, setHasInvalidInputs] = useState(false);

  function setToValueTemplates(valueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>): void {
    const accessibleValueTemplates = valueTemplates.filter((valueTemplate) => {
      assertAndContinue(
        !isInaccessible(valueTemplate.config),
        'Got inaccessible value template in setToValueTemplates'
      );
      return !isInaccessible(valueTemplate.config);
    });

    setParameterValueTemplateFormState(
      accessibleValueTemplates.map((valueTemplate) => {
        const parameterConfig = valueTemplate.config as AnalyticsParameterConfig;
        return {
          valid: true,
          valueTemplate: {
            ...valueTemplate,
            config: parameterConfig,
            valueOperation:
              // We can only edit a USE_EMBEDDING_ITEM parameterValueTemplate if its config type matches
              // the embedding type; otherwise, the value template is invalid and should
              // be modified to USE_VALUE_JSON
              valueTemplate.valueOperation === AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM &&
              getParameterType(parameterConfig) !== embeddingType
                ? AnalyticsParameterValueOperation.USE_VALUE_JSON
                : valueTemplate.valueOperation,
          },
        };
      })
    );
    setHasInvalidInputs(false);
  }

  function setToDashboardValues(
    dashboard: Pick<AnalyticsDashboard, 'parameterConfigs' | 'parameterValues'>
  ): void {
    const vals: Array<ValidatedParameterValueTemplate> = dashboard.parameterValues.map((parameterValue) => ({
      valid: true,
      valueTemplate: {
        __typename: 'AnalyticsParameterValueTemplateNode',
        config: dashboard.parameterConfigs.find((config) => config.id === parameterValue.config.id)!,
        valueOperation: AnalyticsParameterValueOperation.USE_VALUE_JSON,
        valueJson: parameterValue.valueJson,
      },
    }));

    const firstEmbeddingTypeConfig = sortParameterConfigs(dashboard.parameterConfigs).find(
      (config) => getParameterType(config) === embeddingType
    );

    setParameterValueTemplateFormState(
      firstEmbeddingTypeConfig
        ? // If there is a config of the embedding type, set its value template to use the embedding item
          // if it has a value template, or add a value template that uses the embedding item if it does not.
          withUpdatedParameterValueTemplate(
            vals,
            firstEmbeddingTypeConfig,
            null,
            AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM,
            true
          )
        : vals
    );

    setHasInvalidInputs(false);
  }

  function updateParameterValueTemplate(
    config: AnalyticsParameterConfig,
    valueJson: JSONValue,
    valueOperation: AnalyticsParameterValueOperation,
    valid: boolean
  ): void {
    const newState = withUpdatedParameterValueTemplate(
      parameterValueTemplatesRef.current,
      config,
      valueJson,
      valueOperation,
      valid
    );

    setParameterValueTemplateFormState(newState);
    setHasInvalidInputs(newState.some((value) => !value.valid));
  }

  function getParameterValueTemplate(configId: string): AccessibleParameterValueTemplateGraphQL | undefined {
    const validatedValueTemplate = parameterValueTemplates.find(
      (valueTemplate) => valueTemplate.valueTemplate.config.id === configId
    );
    return validatedValueTemplate !== undefined ? validatedValueTemplate.valueTemplate : undefined;
  }

  return {
    parameterValueTemplates: parameterValueTemplates.map(
      (validatedTemplate) => validatedTemplate.valueTemplate
    ),
    hasInvalidInputs,
    setToValueTemplates,
    setToDashboardValues,
    updateParameterValueTemplate,
    getParameterValueTemplate,
  };
}
